<template>
  <panel-layout
    id="message-panel"
    :loading="loading"
    :show-footer="false"
  >
    <template #heading>
      <h2 class="text-lg md:text-xl tracking-tight font-bold leading-none/85 mb-1">
        {{ $t('pages.marketing.sentMessages.title') }}
      </h2>
    </template>

    <div v-if="message" class="h-full">
      <o-scroll class="bg-gray-50 dark:bg-gray-900">
        <div class="h-full flex flex-col">
          <div class="flex flex-col divide-y py-3 text-sm">
            <div>
              <div class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.time') }}
                </p>
                <div class="flex-grow">
                  {{ $date.format($date.local(message.created * 1000), $date.presets.full) }}
                </div>
              </div>
              <div v-if="message.customer" class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.customer') }}
                </p>
                <div class="flex-grow">
                  {{ message.customer.displayName }}
                </div>
              </div>
              <div v-if="isChannelEmail" class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.subject') }}
                </p>
                <div class="flex-grow">
                  {{ message.subject }}
                </div>
              </div>
              <div class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.type') }}
                </p>
                <div class="flex-grow">
                  {{ message.typeLabel }}
                </div>
              </div>
              <div class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('pages.marketing.sentMessages.channel') }}
                </p>
                <div class="flex-grow">
                  {{ $t(MarketingMessageChannelLabels[message.channel]) }}
                </div>
              </div>
              <div class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('general.status') }}
                </p>
                <div class="flex-grow">
                  <div class="flex items-center space-x-1">
                    <o-chip v-if="message.status.bounced" size="sm" flat variant="warning">
                      {{ $t('general.bounced') }}
                    </o-chip>

                    <o-chip v-if="message.status.clicked" size="sm" flat variant="success">
                      {{ $t('general.clicked') }}
                    </o-chip>

                    <o-chip v-if="message.status.complained" size="sm" flat variant="danger">
                      {{ $t('general.complained') }}
                    </o-chip>

                    <o-chip v-if="message.status.delivered" size="sm" flat variant="info">
                      {{ $t('general.delivered') }}
                    </o-chip>

                    <o-chip v-if="message.status.opened" size="sm" flat variant="success">
                      {{ $t('general.opened') }}
                    </o-chip>

                    <o-chip v-if="message.status.unsubscribed" size="sm" flat variant="danger">
                      {{ $t('general.unsubscribed') }}
                    </o-chip>
                  </div>
                </div>
              </div>
              <div v-if="message.cost" class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('pages.marketing.sentMessages.cost') }}
                </p>
                <div class="flex-grow">
                  {{ message.cost.price }} {{ message.cost.unit }}
                </div>
              </div>
              <div v-if="info" class="flex items-center py-2 px-6">
                <p class="opacity-80 text-sm font-medium w-32 flex-shrink-0">
                  {{ $t('pages.marketing.sentMessages.smsInfo') }}
                </p>
                <div class="min-w-0 flex-grow">
                  <p>{{ $t('pages.marketing.sentMessages.provider', { provider: info.provider }) }}</p>
                  <p class="[overflow-wrap:anywhere]">{{ $t('pages.marketing.sentMessages.message', { message: info.message }) }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-grow flex-shrink h-10 m-6 p-3 bg-gray-100 dark:bg-gray-800/20 dark:border rounded-md whitespace-pre-line">
            <o-scroll>
              <div v-html="message.body" />
            </o-scroll>
          </div>
        </div>
      </o-scroll>
    </div>
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { MarketingMessageChannelLabels } from '~/_api/models/marketing'
import { usePanel } from '~/composables/usePanel'

const props = defineProps<{
  channel?: string
  id?: string
}>()

const { $api, $auth, $notification } = useNuxtApp()
const router = useRouter()

const panel = usePanel()

const message = ref<any>(null)
const info = ref<any>(null)
const loading = ref(true)

const isChannelEmail = computed(() => {
  return message.value?.isChannelEmail
})

const isChannelSMS = computed(() => {
  return message.value?.isChannelSMS
})

const messageListPath = computed(() => {
  return props.channel === 'sms' ? '/marketing/messages/sms' : '/marketing/messages/email'
})

const fetchMessage = async () => {
  try {
    const result = (await $api.marketing.sentMessage(props.channel, props.id)).data

    message.value = result
    loading.value = false
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    router.push(messageListPath.value)
  }
}

const fetchSMSInfo = async () => {
  try {
    const result = (await $api.marketing.sentSMSInfo(props.id)).data

    info.value = result
    loading.value = false
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    router.push(messageListPath.value)
  }
}

const init = async () => {
  await fetchMessage()

  if ($auth.info?.adminUser && isChannelSMS.value) {
    await fetchSMSInfo()
  }
}

init()
</script>
